import {filter, keys, last} from 'ramda';
import {Constraint, FieldJson} from 'edocatbase/Form';
import {isInt, isFloat} from '../../../../rpa2/utils/validation';

const moment = window.moment;

export const regexOptions: { [key: string]: RegExp } = {
  'Alfresco.forms.validation.fileName': /([\"\*\\\>\<\?\/\:\|]+)|([\.]?[\.]+$)|(^[ \t]+|[ \t]+$)/,
  'Alfresco.forms.validation.id': /^(?!\-)(?!\_)(?!\.)[A-Za-z0-9\_\-\.]*[A-Za-z0-9]+$/,
  'Alfresco.forms.validation.name': /([\"\*\\\>\<\?\/\:\|\+]+)|([\.]?[\.]+$)|(^[ \t]+|[ \t]+$)/,
  'Alfresco.forms.validation.number': /^-?\d+$/, // whole number ( long )
  'Alfresco.forms.validation.double': /^-?\d+(\.\d+)?$/, // float number ( double )
  'Alfresco.forms.validation.email': /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
  'Alfresco.forms.validation.postalCode': /^\d{3}[ ]?\d{2}$/, // czech postal code (allows space)
  'Alfresco.forms.validation.telephone': /^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){10,14}(\s*)?$/,

  'Alfresco.forms.validation.restricted.fileName': /([\\\/"*><?:|+#&=]+)|([.]?[.]+$)|(^[ \t]+|[ \t]+$)|(^\.)|.{250,}/,
  'Alfresco.forms.validation.restricted.directoryName': /([\\\/"*><?:|+#=]+)|([.&]?[.&]+$)|(^[ \t]+|[ \t]+$)|(^\.)|.{250,}/,
  'Alfresco.forms.validation.integrationKey': /[:{}]|.{250,}$/,
  'Alfresco.forms.validation.qName': /^[0-9]|[^A-Za-z0-9]|.{250,}$/,
};

export const shouldValidate = (constraints: Constraint[], template: string) => {
  return constraints.length > 0 || template.endsWith('date.ftl');
};

const notHasValue = (value: any) =>
  typeof value === undefined ||
  value == null ||
  value === '' ||
  /^\s+[^\s]{0}$/.test(value) ||
  (value.constructor.name === 'Object' && !(value.value || value.date)) ||
  (Array.isArray(value) && value.length === 0) ||
  (Array.isArray(value) && value.filter(item => item.ico === "").length > 0);

const validateCurrentField = (value: any, constraint: Constraint, fields: { [key: string]: FieldJson }) => {
  switch (constraint.id) {
    case 'MANDATORY': {
      return notHasValue(value) ? 'mandatory' : false;
    }

    case 'REGEX': {
      if (notHasValue(value)) return false;

      let regex: RegExp | null = constraint.params?.map?.expression
        ? new RegExp(constraint.params.map.expression, 'g')
        : null;
      const match = constraint.params?.map?.expression?.requiresMatch || false;
      // @ts-ignore
      regex = regex || regexOptions[constraint.validationHandler];

      if (
          constraint.validationHandler === 'Alfresco.forms.validation.fileName' ||
          constraint.validationHandler === 'Alfresco.forms.validation.name'
      ) {
        if ((regex as RegExp).test(value)) {
          if (constraint.validationHandler === 'Alfresco.forms.validation.fileName') {
            return window.Alfresco.messages.global['googledocs.invalidFilename.title'];
          }
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      } else if (constraint.validationHandler === 'Alfresco.forms.validation.email') {
        const valuesArray = value.split(',');
        if (!valuesArray.every((email: string) => (regex as RegExp).test(email.trim()))) {
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      } else if (constraint.validationHandler === 'Alfresco.forms.validation.telephone') {
        if ((regex as RegExp).test(value)) {
        } else {
          return window.Alfresco.messages.global['Alfresco.forms.validation.telephone'];
        }
      } else if (constraint.validationHandler === 'Alfresco.forms.validation.restricted.directoryName') {
        if ((regex as RegExp).test(value)) {
          if (constraint.validationHandler === 'Alfresco.forms.validation.restricted.directoryName') {
            return window.Alfresco.messages.global['googledocs.invalidDirectoryName.title'];
          }
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      } else if (constraint.validationHandler === 'Alfresco.forms.validation.restricted.fileName') {
        if ((regex as RegExp).test(value)) {
          if (constraint.validationHandler === 'Alfresco.forms.validation.restricted.fileName') {
            return window.Alfresco.messages.global['googledocs.invalidFilename.title'];
          }
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      } else if (constraint.validationHandler === 'Alfresco.forms.validation.qName') {
        if ((regex as RegExp).test(value)) {
          if (constraint.validationHandler === 'Alfresco.forms.validation.qName') {
            return window.Alfresco.messages.global['googledocs.invalidFilename.title'];
          }
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      } else {
        if ((match && (regex as RegExp).test(value)) || (!match && !(regex as RegExp).test(value))) {
          return constraint.validationHandler ? last(constraint.validationHandler.split('.')) : 'regexMatch';
        }
      }

      return false;
    }

    case 'NUMBER': {
      if (notHasValue(value)) return false;
      let { min, max } = fields?.[constraint?.fieldId]?.control?.params;
      const dataType = fields?.[constraint?.fieldId].dataType;

      if(!min || Number.MIN_SAFE_INTEGER >= min) {
        min = Number.MIN_SAFE_INTEGER;
      }
      if (!max || Number.MAX_SAFE_INTEGER <= max) {
        max = Number.MAX_SAFE_INTEGER;
      }

      const handler = constraint.validationHandler || '';
      const regex = regexOptions[handler];
      
      if (value.constructor.name === 'Object') {
        // Check if value is a number
        if (!regex.test(value.value)) {
          return last(handler.split('.'));
        }

        if(['double'].includes(dataType)){
          return validateDouble(value, min, max);
        }

        // Check if value is in allowed range
        if ((min || min === 0) && Number(value.value) < min ) {
          
          return `${window.Alfresco.messages.global['Alfresco.forms.validation.minValue']} ${min}`;
        }

        if ((max || max === 0) && Number(value.value) > max) {
          return `${window.Alfresco.messages.global['Alfresco.forms.validation.maxValue']} ${max}`;
        }
      } else {
        // Check if value is a number
        
        if (!regex.test(value)) {
          return last(handler.split('.'));
        }

        if(['double'].includes(dataType)){
          return validateDouble(value, min, max);
        }

        // Check if value is in allowed range
        if ((min || min === 0) && Number(value) < min) {
          return `${window.Alfresco.messages.global['Alfresco.forms.validation.minValue']} ${min}`;
        }

        if ((max || max === 0) && Number(value) > max) {
          return `${window.Alfresco.messages.global['Alfresco.forms.validation.maxValue']} ${max}`;
        }
      }

      return false;
    }

    case 'DATE': {
      if (notHasValue(value)) return false;
      const minDate: Date = fields?.[constraint?.fieldId]?.control?.params?.minDate || null;
      // Check if value is valid
      if (moment(value).isValid()) {
        // Check if minDate does exist
        if (minDate) {
          // Check if minDate
          const isAfter = moment(value).isAfter(moment(minDate).subtract(1, 'day'));
          return isAfter ? false : 'date';
        } else {
          return false;
        }
      } else {
        return 'date';
      }
    }

    case 'SAME_AS': {
      if (notHasValue(value)) return false;

      const fieldSameAs = constraint.fieldSameAs || '';
      if (value !== fields[fieldSameAs]?.value) return 'sameAs';

      return false;
    }

    case 'CONDITIONED_MANDATORY': {
      if (notHasValue(value)) return false;

      const condMandatory = constraint.params?.constraint_conditioned_mandatory || '';
      if (notHasValue(fields[condMandatory].value)) return 'conditionedMandatory';

      return false;
    }

    case 'FORBIDDEN_VALUE': {
      if (notHasValue(value)) return false;

      if (constraint?.forbiddenValues?.includes && constraint.forbiddenValues.includes(value)) {
        return constraint.message;
      }

      return false;
    }

    default: {
      return false;
    }
  }
};

const hasErrors = (validationResults: { [key: string]: string | boolean }) => {
  return Object.values(validationResults).some(it => it);
};

export const validateForm = (fields: { [key: string]: FieldJson }, constraints: Constraint[]) => {
  const validationErrors = constraints.reduce((acc, next) => {
    const fieldId = next.fieldId;
    const field = fields[fieldId];

    if (fieldId && field) {
      const fieldValidationResult = validateCurrentField(field.value, next, fields);

      if (fieldValidationResult) {
        return {
          ...acc,
          [fieldId]: fieldValidationResult,
        };
      }
    }

    return acc;
  }, {});

  return {
    validationErrors,
    hasErrors: hasErrors(validationErrors),
  };
};

export const validateField = (value: any, constraints: Constraint[], fields: { [key: string]: FieldJson }) => {
  return constraints.length > 0
    ? constraints.map(constraint => validateCurrentField(value, constraint, fields)).find(it => it)
    : false;
};

export const formDisabled = (fields: { [key: string]: FieldJson }) =>
  keys(fields).filter(item => Boolean(fields[item]?.error)).length > 0;

export const validateDouble = (val: any, min?: Number, max?: Number) => {
  // validace float viz EDC-3496
  // absolutni pravidlo
  if (!isFloat(val)) {
    return window.Alfresco.messages.global['Alfresco.forms.validation.double.message'];
  }

  //double "max safe value" je cca 14 mist po sobe jdoucich cisel v radu  
  if ((val?.replace(/^[0.,]*|[,.]|0*$/gi, "")?.length > 14)) {
    return `${window.Alfresco.messages.global['Alfresco.forms.validation.safeDoubleNumber']}`;
  }

  // optional limits
  if (max && (parseFloat(val) > parseFloat(max.toString()))) {
    return `${window.Alfresco.messages.global['Alfresco.forms.validation.maxValue']} ${max}`;
  } else if (min &&  (parseFloat(val) < parseFloat(min.toString()))) {
    return `${window.Alfresco.messages.global['Alfresco.forms.validation.minValue']} ${min}`;
  }

  return false;
}